import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Nango from '@nangohq/frontend';
import { ArrowRight, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from '../components/ui/sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleAccessPage() {
  const { user, authFetch, loading } = useAuth();
  const [busyProvider, setBusyProvider] = useState('');

  const handleGoogleStart = async (provider) => {
    if (!user) {
      toast.error('Primero entra con tu acceso actual y luego conecta Google.');
      return;
    }
    try {
      setBusyProvider(provider);
      const response = await authFetch(`${API}/connections/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo abrir la conexión de Google');
      const nango = new Nango();
      const connectUi = nango.openConnectUI({
        baseURL: data.nango_connect_ui_base_url,
        apiURL: data.nango_api_base_url,
        lang: 'es',
        themeOverride: 'dark'
      });
      connectUi.setSessionToken(data.session_token);
    } catch (error) {
      toast.error(error.message || 'No se pudo abrir la conexión de Google');
    } finally {
      setBusyProvider('');
    }
  };

  return (
    <>
      <SEO title="Conectar Google | Neuralia369" description="Ruta corta para conectar Google con Nango sin depender de enlaces OAuth largos en el chat." path="/conectar-google" />
      <main className="min-h-screen bg-background px-4 py-16 sm:px-6 lg:px-8" data-testid="google-access-page">
        <div className="mx-auto max-w-3xl">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="glass-card tracing-panel p-8 md:p-10" data-testid="google-access-card">
            <p className="editorial-kicker mb-4">Google / Gmail por Nango</p>
            <h1 className="text-4xl sm:text-5xl text-text-heading" data-testid="google-access-title">Conecta tu cuenta Google desde una ruta corta y operativa</h1>
            <p className="mt-4 text-lg text-text-body" data-testid="google-access-body">Esta vía usa el Hub de Conexiones que ya está activo en Neuralia369. Evita pegar enlaces OAuth largos en el chat y abre la autorización real de Google/Gmail desde una capa más estable para operar ya.</p>

            <div className="mt-6 rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="google-access-benefits-card">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-1 w-5 h-5 text-primary" />
                <div>
                  <p className="text-text-heading font-semibold">Qué resuelve esta vía</p>
                  <p className="mt-2 text-sm text-text-body">Conexión real de Gmail / cuenta Google desde el Hub de Conexiones embebido. Primero entra con tu usuario actual y después conecta la cuenta Google que quieras usar.</p>
                </div>
              </div>
            </div>

            {!loading && !user && (
              <div className="mt-6 rounded-[22px] border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="google-access-login-required">
                Para conectar Google primero debes entrar con tu acceso actual. Después vuelve aquí y abre la autorización con un clic.
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" onClick={() => handleGoogleStart('gmail')} className="btn-primary" data-testid="google-access-start-button" disabled={loading || busyProvider === 'gmail'}>
                <Mail className="w-4 h-4" /> {busyProvider === 'gmail' ? 'Abriendo Google / Gmail…' : 'Conectar Google / Gmail'}
              </button>
              <Link to="/acceso-admin" className="btn-secondary" data-testid="google-access-admin-link">Ir al acceso admin <ArrowRight className="w-4 h-4" /></Link>
            </div>

            <p className="mt-4 text-xs text-text-muted" data-testid="google-access-footnote">Para operar ya, esta conexión por Nango es la vía más sólida disponible. Drive/Calendar adicionales se pueden ampliar después si quieres más permisos o más casos de uso.</p>
          </motion.section>
        </div>
      </main>
    </>
  );
}